/**
 * Calculates the alignment distance between two strings using the Levenshtein
 * distance algorithm. This distance is defined as the minimum number of
 * single-character edits (insertions, deletions, or substitutions) required to
 * change one string into the other. The function implements a dynamic
 * programming approach to efficiently compute this metric.
 *
 * @param {string} a - The first string to compare.
 * @param {string} b - The second string to compare.
 * @returns {number} The alignment distance between the two strings. This is a non-negative integer where
 * a value of 0 indicates that the strings are identical, and a larger value
 * indicates a greater degree of difference.
 *
 * @example
 * // returns 3
 * alignment("kitten", "sitting");
 *
 * @example
 * // returns 2
 * alignment("flaw", "lawn");
 */
export function alignment(a: string, b: string) {
  const m = a.length;
  const n = b.length;
  const dp = Array.from({ length: m + 1 }, () => Array<number>(n + 1).fill(0));

  // Initialize the first column and the first row of the matrix
  for (let i = 0; i <= m; i++) dp[i][0] = i;
  for (let j = 0; j <= n; j++) dp[0][j] = j;

  // Fill dp matrix
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1, // deletion
        dp[i][j - 1] + 1, // insertion
        dp[i - 1][j - 1] + cost, // substitution
      );
    }
  }

  // Return the value in the bottom right corner
  return dp[m][n];
}
